import React, { useEffect, useRef } from 'react';

import { X } from 'lucide-react';
import ReactDOM from 'react-dom';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'small' | 'large';
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  title,
  size,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const html = document.querySelector('html');
    if (html) {
      html.style.overflowY = isOpen ? 'hidden' : 'visible';
    }
    return () => {
      if (html) {
        html.style.overflowY = 'visible';
      }
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const getDrawerSize = () => {
    switch (size) {
      case 'small':
        return 'max-w-full md:max-w-md'; // Full width on mobile, 28rem / 448px on md+
      case 'large':
        return 'max-w-full md:max-w-6xl'; // Full width on mobile, 72rem / 1152px on md+
      default:
        return 'max-w-full md:max-w-md'; // Full width on mobile, 32rem / 512px on md+
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[81] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-secondary/40 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 md:pl-10">
          <div
            ref={drawerRef}
            className={`w-screen ${getDrawerSize()} transform transition-transform duration-500 ease-in-out`}
          >
            <div className="flex h-full flex-col overflow-y-auto bg-white dark:bg-gray-800 shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {title}
                  </h2>
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="relative flex-1 px-6 py-4">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Drawer;
